import { useEffect, useState } from "react";
import { fetchReviews } from "@/lib/api";
import useReveal from "@/hooks/useReveal";

const PER_PAGE = 5;

function Stars({ value = 0, size = "md" }) {
  const filled = Math.round(value);
  const sz =
    size === "lg" ? "text-2xl" : size === "sm" ? "text-sm" : "text-base";
  return (
    <span
      className={`inline-flex tracking-[0.15em] ${sz}`}
      aria-label={`${value} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={n <= filled ? "text-[#d4af37]" : "text-[#3a3a3a]"}
        >
          ★
        </span>
      ))}
    </span>
  );
}

function HistogramBar({ rating, percentage, frequency }) {
  return (
    <div className="flex items-center gap-3 text-xs font-label">
      <span className="text-[#8a8a85] w-6 text-right">{rating}★</span>
      <div className="flex-1 h-[3px] bg-[#1a1a1a] overflow-hidden">
        <div
          className="h-full bg-[#d4af37]"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-[#8a8a85] w-8">{frequency}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function Reviews({ slug }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const reveal = useReveal();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    fetchReviews(slug, { page, perPage: PER_PAGE })
      .then((d) => {
        if (mounted) setData(d);
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug, page]);

  if (loading && !data) {
    return (
      <section
        data-testid="product-reviews"
        className="mt-24 border-t border-[#1f1f1f] pt-16"
      >
        <span className="font-label text-[#8a8a85]">Loading reviews…</span>
      </section>
    );
  }

  if (error || !data || !data.product_id) {
    return null;
  }

  const {
    average_rating,
    number_of_reviews,
    histogram = [],
    reviews = [],
    total_pages = 1,
    current_page = 1,
    write_review_url,
  } = data;

  if (number_of_reviews === 0) {
    return (
      <section
        ref={reveal.ref}
        data-testid="product-reviews"
        className={`reveal ${reveal.inView ? "in-view" : ""} mt-24 border-t border-[#1f1f1f] pt-16`}
      >
        <span className="font-label text-[#8a8a85]">Reviews</span>
        <h2 className="mt-3 font-display text-3xl">No reviews yet.</h2>
        <p className="mt-3 text-[#a3a3a3] font-light max-w-md">
          Be the first to share your impression of The Africa Frame™.
        </p>
        {write_review_url && (
          <a
            href={write_review_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="write-review-link"
            className="mt-8 inline-block btn-ghost-gold"
          >
            Write the first review →
          </a>
        )}
      </section>
    );
  }

  return (
    <section
      ref={reveal.ref}
      data-testid="product-reviews"
      className={`reveal ${reveal.inView ? "in-view" : ""} mt-24 border-t border-[#1f1f1f] pt-16`}
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
        <div>
          <span className="font-label text-[#8a8a85]">Reviews</span>
          <h2 className="mt-3 font-display text-[clamp(1.8rem,3vw,2.6rem)] leading-tight">
            What collectors are saying
          </h2>
        </div>
        {write_review_url && (
          <a
            href={write_review_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="write-review-link"
            className="btn-ghost-gold self-start lg:self-end"
          >
            Write a review →
          </a>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 pb-10 border-b border-[#1f1f1f]">
        <div className="lg:col-span-4 flex flex-col items-start">
          <span className="font-label text-[#8a8a85]">Average rating</span>
          <p
            className="mt-3 font-editorial italic gold-text-gradient text-6xl leading-none"
            data-testid="reviews-average-rating"
          >
            {average_rating.toFixed(2)}
          </p>
          <div className="mt-3">
            <Stars value={average_rating} size="lg" />
          </div>
          <p
            className="mt-3 font-label text-[#a3a3a3]"
            data-testid="reviews-count"
          >
            Based on {number_of_reviews}{" "}
            {number_of_reviews === 1 ? "review" : "reviews"}
          </p>
        </div>
        <div className="lg:col-span-8 space-y-2.5">
          {histogram.map((h) => (
            <HistogramBar
              key={h.rating}
              rating={h.rating}
              frequency={h.frequency}
              percentage={h.percentage}
            />
          ))}
        </div>
      </div>

      {/* Review list */}
      <ul className="space-y-10" data-testid="reviews-list">
        {reviews.map((r) => (
          <li
            key={r.id}
            data-testid={`review-item-${r.id}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 pb-10 border-b border-[#1a1a1a] last:border-b-0"
          >
            <div className="md:col-span-3 flex md:block items-center gap-4">
              <div className="w-12 h-12 border border-[#d4af37]/40 text-[#d4af37] font-display flex items-center justify-center">
                {r.reviewer_initial ||
                  (r.reviewer_name ? r.reviewer_name[0] : "·")}
              </div>
              <div className="md:mt-4">
                <p className="font-display text-white text-base">
                  {r.reviewer_name || "Anonymous"}
                </p>
                <p className="font-label text-[#6a6a65] text-[10px] mt-1">
                  {formatDate(r.created_at)}
                </p>
                {r.verified_buyer && (
                  <p className="font-label text-[#d4af37] text-[10px] mt-2">
                    Verified buyer
                  </p>
                )}
              </div>
            </div>
            <div className="md:col-span-9">
              <Stars value={r.rating} size="sm" />
              {r.title && (
                <h3 className="mt-3 font-display text-white text-xl leading-tight">
                  {r.title}
                </h3>
              )}
              {r.body_html && (
                <div
                  className="mt-3 text-[#bdbdb7] font-light leading-relaxed [&>p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: r.body_html }}
                />
              )}
              {r.pictures_urls && r.pictures_urls.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {r.pictures_urls.map((src, i) =>
                    src ? (
                      <img
                        key={`${r.id}-pic-${i}`}
                        src={src}
                        alt=""
                        className="w-20 h-20 object-cover border border-[#2a2a2a]"
                      />
                    ) : null
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Pagination */}
      {total_pages > 1 && (
        <div
          className="mt-12 flex items-center justify-between gap-4"
          data-testid="reviews-pagination"
        >
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={current_page <= 1 || loading}
            data-testid="reviews-prev"
            className="font-label text-[#8a8a85] hover:text-[#d4af37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            ← Previous
          </button>
          <span className="font-label text-[#6a6a65] text-xs">
            Page {current_page} of {total_pages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(total_pages, p + 1))}
            disabled={current_page >= total_pages || loading}
            data-testid="reviews-next"
            className="font-label text-[#8a8a85] hover:text-[#d4af37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </section>
  );
}
